import { Component, computed, inject, OnInit, signal, HostListener, LOCALE_ID, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { ApprovedPhFilters } from './approved-ph-filters/approved-ph-filters';
import { ApprovedPhTable, LoadState } from './approved-ph-table/approved-ph-table';
import { ApprovedPhTelesalesFilters } from './approved-ph-telesales-filters/approved-ph-telesales-filters';
import { FollowUpDialog } from './follow-up-dialog/follow-up-dialog';
import { AuthService } from '../../../services/auth-service';
import { ROLE_IDS } from '../../../services/role/role-constants';
import { ApprovedPharmaciesService } from '../../../services/pharmacy/approved-pharmacies.service';
import { PharmacyService } from '../../../services/pharmacy/pharmacy.service';
import {
  ApprovedPharmacyFilters,
  ApprovedPharmacyListItem,
} from '../../../pages/admin-dashboard/admin-approved-pharmacies/approved-pharmacies.model';
import { LookupVTO } from '../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { LookupService } from '../../../services/pharmacy/lookup.service';
import { PharmacySegmentVTO } from '../../../services/pharmacy/lookup.model';

@Component({
  selector: 'app-admin-approved-ph-component',
  imports: [ApprovedPhFilters, ApprovedPhTable, ApprovedPhTelesalesFilters, FollowUpDialog],
  templateUrl: './admin-approved-ph-component.html',
  styleUrl: './admin-approved-ph-component.scss',
  standalone: true,
})
export class AdminApprovedPhComponent implements OnInit {
  @ViewChild(ApprovedPhFilters) filtersComponent!: ApprovedPhFilters;

  private approvedPharmaciesService = inject(ApprovedPharmaciesService);
  private pharmacyService = inject(PharmacyService);
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  private lookupService = inject(LookupService);
  private authService = inject(AuthService);

  isTeleSales = computed(() => this.authService.hasRoleId(ROLE_IDS.TELE_SALES));
  segments = signal<PharmacySegmentVTO[]>([]);

  followUpPharmacy = signal<ApprovedPharmacyListItem | null>(null);

  isDropDown = signal<number | null>(null);
  isFiltersApplied = signal<boolean>(false);

  state = signal<LoadState>('loading');
  pharmacies = signal<ApprovedPharmacyListItem[] | null>(null);
  currentPage = signal<number>(1);
  itemsPerPage = signal<number>(this.isTeleSales() ? 20 : 25);
  totalItems = signal<number>(0);

  filters = signal<ApprovedPharmacyFilters>(this.defaultFilters());

  private defaultFilters(): ApprovedPharmacyFilters {
    return { orderBy: 'LAST_MODIFICATION_DATE', orderDir: 'DESC' };
  }

  ngOnInit() {
    this.loadSegments();
    this.loadPharmacies();
  }

  loadSegments() {
    this.lookupService.getPharmacySegments().subscribe({
      next: (res) => this.segments.set(res.list),
      error: () => {},
    });
  }

  loadPharmacies() {
    this.filters.update((f) => ({ ...f, pageNum: this.currentPage() - 1, pageSize: this.itemsPerPage() }));
    this.state.set('loading');
    this.approvedPharmaciesService.getApprovedPharmacies(this.filters()).subscribe({
      next: (res) => {
        this.pharmacies.set(res.list);
        this.totalItems.set(res.total);
        this.state.set('success');
      },
      error: () => this.state.set('error'),
    });
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems() / this.itemsPerPage());
  }

  toggleSortOrder() {
    this.filters.update((f) => ({ ...f, orderDir: f.orderDir === 'ASC' ? 'DESC' : 'ASC' }));
    this.loadPharmacies();
  }

  applyFilters(applied: {
    pharmacyId?: number;
    statusIds?: number[];
    representativeId?: number;
    createdOnFrom?: string;
    createdOnTo?: string;
    lastModifiedOnFrom?: string;
    lastModifiedOnTo?: string;
    pharmacySegmentIds?: number[];
    minCredit?: number;
    maxCredit?: number;
    governorateIds?: number[];
    cityId?: number;
    regionId?: number;
  }) {
    this.currentPage.set(1);
    this.isFiltersApplied.set(true);
    this.filters.update((f) => ({ ...f, ...applied, pageNum: 0, pageSize: this.itemsPerPage() }));
    this.loadPharmacies();
  }

  resetFilters() {
    this.filtersComponent?.resetFilters;
    this.filters.set(this.defaultFilters());
    this.isFiltersApplied.set(false);
    this.currentPage.set(1);
    this.loadPharmacies();
  }

  selectPharmacyOwner(item: { id: number; fullName: string }) {
    this.filters.update((f) => ({ ...f, pharmacyOwnerId: item.id }));
  }

  selectEmployee(item: { id: number; fullName: string }) {
    this.filters.update((f) => ({ ...f, assignedToId: item.id }));
  }

  onFollowUpSaved() {
    this.followUpPharmacy.set(null);
    this.loadPharmacies();
  }

  onAssignSuccess(pharmacyId: number) {
    this.loadPharmacies();
  }

  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage.set(page);
      this.loadPharmacies();
    }
  }

  onPageSizeChange(size: number) {
    this.itemsPerPage.set(size);
    this.currentPage.set(1);
    this.loadPharmacies();
  }

  // navigateToDetails(id: number) {
  //   this.router.navigate(['/admin/approved-pharmacies/view', id]);
  // }
  navigateToDetails(pharmacy: ApprovedPharmacyListItem) {
  this.router.navigate(
    ['/admin/approved-pharmacies/view', pharmacy.id],
    {
      state: {
        pharmacyName: pharmacy.nickName,
      },
    }
  );
}

  openDropDown({ entityId }: { entityId?: number }) {
    this.isDropDown.set(entityId!);
  }

  closeDropDown() {
    this.isDropDown.set(null);
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.isDropDown.set(null);
  }

  isArabic() {
    return this.locale.startsWith('ar');
  }
}
